import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertContactRequestSchema } from "@shared/schema";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CheckCircle2, Phone, Mail, MapPin, Clock } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, getApiUrl } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const formSchema = insertContactRequestSchema.extend({
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
});

type FormData = z.infer<typeof formSchema>;

export default function ContactForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service: undefined,
      message: "",
      preferredDate: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetch(getApiUrl("/api/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to submit request");
      }
      return response.json();
    },
    onSuccess: () => {
      form.clearErrors(); // Clear any validation errors
      form.reset(); // Reset form values
      setIsSubmitted(true); // Show success message
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    mutation.mutate(data);
  };

  const contactInfo = [
    { icon: Phone, label: "Phone", value: "(832) 998-9595" },
    { icon: Mail, label: "Email", value: "info@hhsbuild.com" },
    { icon: MapPin, label: "Service Area", value: "Houston, TX" },
    { icon: Clock, label: "Hours", value: "24/7 Support" },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 relative" data-testid="section-contact">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4 tracking-tight gradient-text"
            data-testid="text-contact-title"
          >
            Get Your Free Estimate
          </h2>
          <p
            className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto"
            data-testid="text-contact-subtitle"
          >
            Schedule a free measurement visit and receive a detailed quote for your project
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <Card className="p-6 md:p-8 glass-card border-cyan-500/20">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12" data-testid="text-success-message">
                <div className="bg-cyan-500/20 w-20 h-20 rounded-full flex items-center justify-center mb-6 glow-cyan animate-pulse">
                  <CheckCircle2 className="h-10 w-10 text-cyan-400" />
                </div>
                <h3 className="text-2xl font-heading font-bold mb-2 gradient-text">Thank You!</h3>
                <p className="text-gray-300 text-center">
                  We'll contact you within 24 hours to schedule your free measurement visit.
                </p>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} data-testid="input-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="john@example.com"
                              {...field}
                              data-testid="input-email"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="(555) 123-4567" {...field} data-testid="input-phone" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="service"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-service">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Bathroom Renovation">Bathroom Renovation</SelectItem>
                            <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                            <SelectItem value="Whole-Home Renovation">Whole-Home Renovation</SelectItem>
                            <SelectItem value="Handyman / Emergency Repair">Handyman / Emergency Repair</SelectItem>
                            <SelectItem value="Custom Woodwork">Custom Woodwork</SelectItem>
                            <SelectItem value="Property Maintenance Plan">Property Maintenance Plan</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferredDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Date (Optional)</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} value={field.value || ""} data-testid="input-preferred-date" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Project Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us about your project..."
                            className="resize-none h-32"
                            {...field}
                            data-testid="input-message"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full font-semibold glow-cyan" 
                    data-testid="button-submit"
                    disabled={mutation.isPending}
                  >
                    {mutation.isPending ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      "Request Free Estimate"
                    )}
                  </Button>
                </form>
              </Form>
            )}
          </Card>

          <div className="space-y-6">
            <Card className="p-6 md:p-8 glass-card border-cyan-500/20">
              <h3 className="text-2xl font-heading font-bold mb-6 gradient-text" data-testid="text-contact-info-title">
                Contact Information
              </h3>
              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="flex items-start gap-4" data-testid={`text-contact-info-${index}`}>
                      <div className="bg-cyan-500/10 p-3 rounded-md flex-shrink-0 border border-cyan-500/20">
                        <Icon className="h-5 w-5 text-cyan-400" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-400">{info.label}</div>
                        <div className="font-semibold text-gray-100">{info.value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-6 md:p-8 glass-card border-gold-500/20 bg-gradient-to-br from-gold-500/5 to-transparent">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-gold-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1 text-gray-100">Free Measurement Visit</h4>
                    <p className="text-sm text-gray-300">
                      We'll visit your location to take accurate measurements at no cost
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-gold-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1 text-gray-100">Detailed Quote</h4>
                    <p className="text-sm text-gray-300">
                      Receive a comprehensive breakdown of costs and timeline
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-gold-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1 text-gray-100">24-Hour Response</h4>
                    <p className="text-sm text-gray-300">
                      We'll get back to you within one business day
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16" data-testid="section-faq">
          <h3 className="text-3xl md:text-4xl font-heading font-bold gradient-text mb-8 text-center">
            Frequently Asked Questions
          </h3>
          <Card className="glass-card border-cyan-500/20">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" data-testid="faq-item-1">
                <AccordionTrigger className="text-left px-6 hover:no-underline hover:text-[#00d9ff] transition-colors">
                  <span className="text-lg font-semibold text-gray-200">Is the estimate really free?</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-400">
                  Yes. Our in-home visit and written estimate are 100% free with no obligation. We'll measure your space, discuss your goals, and provide a detailed quote—all at no cost.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" data-testid="faq-item-2">
                <AccordionTrigger className="text-left px-6 hover:no-underline hover:text-[#00d9ff] transition-colors">
                  <span className="text-lg font-semibold text-gray-200">How soon will you contact me?</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-400">
                  We respond to all estimate requests within one business day (Mon–Fri). For emergency repairs, our 24/7 handyman line is available anytime.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" data-testid="faq-item-3">
                <AccordionTrigger className="text-left px-6 hover:no-underline hover:text-[#00d9ff] transition-colors">
                  <span className="text-lg font-semibold text-gray-200">What's included in the 1-year warranty?</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-400">
                  All remodeling projects include a 12-month labor warranty on workmanship. Materials are covered by manufacturer warranties. We stand behind our work and will address any issues that arise.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border-b-0" data-testid="faq-item-4">
                <AccordionTrigger className="text-left px-6 hover:no-underline hover:text-[#00d9ff] transition-colors">
                  <span className="text-lg font-semibold text-gray-200">When can you start my project?</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-gray-400">
                  Start dates vary by season, but most projects begin within 2–6 weeks of contract signing. We'll provide a realistic timeline during your estimate visit.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
        </div>
      </div>
    </section>
  );
}
